import React, { useState } from 'react';
import CheckIcon from '../../icons/status/CheckIcon';

interface PasswordFieldProps {
  id: string;
  name: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  autoComplete?: string;
  disabled?: boolean;
  className?: string;
  autoFocus?: boolean;
  showValidation?: boolean;
  serverErrors?: string[];
}

interface ValidationRule {
  label: string;
  test: (password: string) => boolean;
  serverErrorKeywords: string[];
}

const validationRules: ValidationRule[] = [
  {
    label: 'Password is required.',
    test: (password: string) => password.length > 0,
    serverErrorKeywords: ['required', 'blank', 'empty'],
  },
  {
    label: 'Your password must contain a lowercase letter.',
    test: (password: string) => /[a-z]/.test(password),
    serverErrorKeywords: ['lowercase'],
  },
  {
    label: 'Your password must contain an uppercase letter.',
    test: (password: string) => /[A-Z]/.test(password),
    serverErrorKeywords: ['uppercase'],
  },
  {
    label: 'Your password must contain a number.',
    test: (password: string) => /\d/.test(password),
    serverErrorKeywords: ['digit', 'number'],
  },
  {
    label: 'Your password must contain a symbol.',
    test: (password: string) => /[^a-zA-Z\d\s]/.test(password),
    serverErrorKeywords: ['special character', 'symbol'],
  },
  {
    label: 'Your password must contain at least 8 characters.',
    test: (password: string) => password.length >= 8,
    serverErrorKeywords: ['8 character', 'too short', 'minimum'],
  },
];

const PasswordField: React.FC<PasswordFieldProps> = ({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  required = false,
  autoComplete,
  disabled = false,
  className = '',
  autoFocus = false,
  showValidation = true,
  serverErrors = [],
}) => {
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [hasServerErrors, setHasServerErrors] = useState(false);

  // Track when server errors are received
  React.useEffect(() => {
    if (serverErrors && serverErrors.length > 0) {
      setHasServerErrors(true);
    }
  }, [serverErrors]);

  const shouldShowList = hasBeenFocused;

  // Check if a validation rule matches any server error
  const matchesServerError = (rule: ValidationRule): boolean => {
    if (!serverErrors || serverErrors.length === 0) return false;
    return serverErrors.some((error) => {
      const lowerError = error.toLowerCase();
      return rule.serverErrorKeywords.some((keyword) =>
        lowerError.includes(keyword.toLowerCase())
      );
    });
  };

  // Red border only shows when there are server errors and user hasn't changed the field
  const hasServerValidationErrors =
    hasServerErrors && serverErrors && serverErrors.length > 0;

  // Handle input change - clear server error state when user types
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasServerErrors(false);
    onChange(e);
  };

  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
      >
        {label}
      </label>
      <div className="mt-1">
        <input
          id={id}
          name={name}
          type="password"
          autoComplete={autoComplete}
          required={required}
          value={value}
          onChange={handleChange}
          onFocus={() => setHasBeenFocused(true)}
          className={`appearance-none block w-full px-3 py-2 border rounded-md placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ${
            hasServerValidationErrors
              ? 'border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500'
          }`}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
        />
      </div>
      {showValidation && (
        <div className="mt-2">
          {!shouldShowList ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Use 8+ characters with at least one uppercase letter, lowercase
              letter, number, and symbol.
            </p>
          ) : (
            <ul className="space-y-1">
              {validationRules.map((rule, index) => {
                const isValid = rule.test(value);
                const hasServerError =
                  hasServerErrors && matchesServerError(rule);

                return (
                  <li
                    key={index}
                    className="flex items-start text-xs text-gray-600 dark:text-gray-400"
                  >
                    <span className="mr-2 mt-0.5 flex-shrink-0">
                      {isValid && !hasServerError ? (
                        <CheckIcon size={12} className="text-green-500" />
                      ) : (
                        <span
                          className={
                            hasServerError
                              ? 'text-red-500 dark:text-red-400'
                              : 'text-gray-400 dark:text-gray-500'
                          }
                        >
                          -
                        </span>
                      )}
                    </span>
                    <span
                      className={
                        isValid && !hasServerError
                          ? 'text-green-600 dark:text-green-500'
                          : hasServerError
                            ? 'text-red-600 dark:text-red-400'
                            : ''
                      }
                    >
                      {rule.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default PasswordField;
